import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Users, Globe, ImagePlus, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { uploadPhoto } from "@/components/photoUpload";
import { ContentReactions, ReactedBy, ReportLink, Comments, errText, when } from "@/components/ContentBits";

const POST_MAX = 1500;
const MAX_PHOTOS = 4;

export function StarsInput({ value, onChange }) {
  return (
    <span className="inline-flex items-center gap-1" role="radiogroup" aria-label="Your rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" role="radio" aria-checked={value === n} aria-label={`${n} star${n > 1 ? "s" : ""}`} data-testid={`post-star-${n}`}
          onClick={() => onChange(value === n ? 0 : n)} className={`p-0.5 text-2xl leading-none ${value >= n ? "text-[#C89B3C]" : "text-muted-foreground/40"}`}>★</button>
      ))}
    </span>
  );
}

function StarsRead({ value }) {
  if (!value) return null;
  return <span className="text-[#C89B3C]" aria-label={`${value} out of 5 stars`} data-testid="post-rating">{"★".repeat(value)}<span className="text-muted-foreground/40">{"★".repeat(5 - value)}</span></span>;
}

/** Full-screen photo viewer. Drawn straight into the page body so the top bar's frosted glass can't trap it. */
export function Lightbox({ images, index, onIndex, onClose }) {
  const n = images.length;
  const go = (d) => onIndex((index + d + n) % n);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && n > 1) go(-1);
      else if (e.key === "ArrowRight" && n > 1) go(1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });
  const im = images[index];
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-3" role="dialog" aria-modal="true" aria-label="Photo" data-testid="lightbox" onClick={onClose}>
      <img src={im.url} alt={`Photo ${index + 1} of ${n}`} data-testid="lightbox-image" className="max-h-full max-w-full rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
      <button type="button" onClick={onClose} aria-label="Close photo" data-testid="lightbox-close" className="absolute right-3 top-3 rounded-full bg-black/60 p-2.5 text-white"><X className="h-5 w-5" /></button>
      {n > 1 && (
        <>
          <button type="button" onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Previous photo" data-testid="lightbox-prev" className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2.5 text-white"><ChevronLeft className="h-6 w-6" /></button>
          <button type="button" onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Next photo" data-testid="lightbox-next" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2.5 text-white"><ChevronRight className="h-6 w-6" /></button>
          <p className="absolute bottom-4 left-0 right-0 text-center text-sm text-white/80" data-testid="lightbox-count">{index + 1} / {n}</p>
        </>
      )}
    </div>,
    document.body
  );
}

/** The photos on a post. Tap one to see it full size. */
export function PostImages({ images, who, what = "post" }) {
  const [at, setAt] = useState(null);
  if (!images || images.length === 0) return null;
  const one = images.length === 1;
  return (
    <>
      <div className={`mt-3 grid gap-1.5 ${one ? "" : "grid-cols-2"}`} data-testid="post-images">
        {images.map((im, i) => (
          <button key={im.id} type="button" onClick={() => setAt(i)} data-testid="post-image" aria-label={`Open photo ${i + 1} of ${images.length}`} className="overflow-hidden rounded-xl border border-border bg-background">
            <img src={one ? im.url : im.thumb} alt={who ? `Photo ${i + 1} from ${who}'s ${what}` : `Photo ${i + 1} from your ${what}`} loading="lazy" className={one ? "max-h-[28rem] w-full object-cover" : "aspect-square w-full object-cover"} />
          </button>
        ))}
      </div>
      {at !== null && <Lightbox images={images} index={at} onIndex={setAt} onClose={() => setAt(null)} />}
    </>
  );
}

/** Write a post (or edit one when `initial` is given). ShowPicker is the site's show chooser. */
export function PostComposer({ ShowPicker, initial, presetShow, defaultVisibility = "friends", onPosted, onCancel }) {
  const blank = { body: "", rating: 0, spoiler: false, comments_off: false, show_id: presetShow?.id || "", show_name: presetShow?.name || "", visibility: defaultVisibility };
  const [f, setF] = useState(initial
    ? { body: initial.body, rating: initial.rating || 0, spoiler: !!initial.spoiler, comments_off: !!initial.comments_off, show_id: initial.show_id || "", show_name: initial.show_name || "", visibility: initial.visibility === "public" ? "public" : "friends" }
    : blank);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState(initial?.images || []);
  const [uploading, setUploading] = useState(0);
  const fresh = useRef(new Set());      // photos uploaded in this box and not yet posted
  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const room = MAX_PHOTOS - photos.length - uploading;
    if (files.length > room) toast.error(room > 0 ? `You can add ${room} more photo${room === 1 ? "" : "s"} to this post.` : `A post can have up to ${MAX_PHOTOS} photos.`);
    for (const file of files.slice(0, Math.max(room, 0))) {
      setUploading((n) => n + 1);
      try {
        const v = await uploadPhoto(file, "post");
        fresh.current.add(v.id);
        setPhotos((a) => [...a, v]);
      } catch (err) { toast.error(errText(err, "Could not upload that photo. Please try another.")); }
      finally { setUploading((n) => n - 1); }
    }
  };
  const removePhoto = (p) => {
    setPhotos((a) => a.filter((x) => x.id !== p.id));
    if (fresh.current.delete(p.id)) api.delete(`/media/${p.id}`).catch(() => {});   // never posted, so tidy it away now
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!f.body.trim() || uploading > 0) return;
    setBusy(true);
    const body = { body: f.body, rating: f.rating, spoiler: f.spoiler, comments_off: f.comments_off, visibility: f.visibility, show_id: f.show_id || null, media_ids: photos.map((p) => p.id) };
    try {
      const r = initial ? await api.put(`/posts/${initial.id}`, body) : await api.post("/posts", body);
      toast.success(initial ? "Post updated" : f.visibility === "public" ? "Posted for everyone" : "Posted to your friends");
      if (!initial) { setF(blank); setPhotos([]); }
      fresh.current.clear();
      onPosted(r.data);
    } catch (err) { toast.error(errText(err, "Could not post that. Please try again.")); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border border-[#C89B3C]/40 bg-card p-4 sm:p-5" data-testid="post-composer">
      <label htmlFor="post-body" className="sr-only">Write a post</label>
      <textarea id="post-body" data-testid="post-body" rows={3} maxLength={POST_MAX} value={f.body} onChange={(e) => set({ body: e.target.value })}
        placeholder="Seen something? Tell your friends what you thought…" className="w-full rounded-xl border border-border bg-background px-4 py-3 text-base leading-relaxed focus:border-[#C89B3C] focus:outline-none" />
      <div className="mt-3" data-testid="post-photos">
        {(photos.length > 0 || uploading > 0) && (
          <div className="mb-2 grid grid-cols-4 gap-2">
            {photos.map((p) => (
              <div key={p.id} className="relative aspect-square overflow-hidden rounded-lg border border-border" data-testid="post-photo-thumb">
                <img src={p.thumb || p.url} alt="Attached photo" className="h-full w-full object-cover" />
                <button type="button" onClick={() => removePhoto(p)} aria-label="Remove photo" data-testid="post-photo-remove" className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white"><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            {Array.from({ length: uploading }).map((_, i) => <div key={`u${i}`} className="aspect-square animate-pulse rounded-lg bg-secondary" data-testid="post-photo-uploading" />)}
          </div>
        )}
        {photos.length + uploading < MAX_PHOTOS && (
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-medium text-[#C89B3C] underline">
            <ImagePlus className="h-4 w-4" aria-hidden="true" /> Add photos
            <input type="file" accept="image/*" multiple className="sr-only" data-testid="post-photo-input" onChange={pick} />
          </label>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
        {f.show_id
          ? <span>About <strong>{f.show_name}</strong> {!presetShow && <button type="button" className="ml-1 underline" onClick={() => set({ show_id: "", show_name: "" })}>Remove</button>}</span>
          : !ShowPicker ? null : <button type="button" data-testid="post-pick-show" className="font-medium text-[#C89B3C] underline" onClick={() => setPicking(true)}>Tag a show</button>}
        <StarsInput value={f.rating} onChange={(rating) => set({ rating })} />
        <label className="flex items-center gap-2"><input type="checkbox" data-testid="post-spoiler" checked={f.spoiler} onChange={(e) => set({ spoiler: e.target.checked })} /> Contains spoilers</label>
        <label className="flex items-center gap-2"><input type="checkbox" data-testid="post-comments-on" checked={!f.comments_off} onChange={(e) => set({ comments_off: !e.target.checked })} /> Allow comments</label>
      </div>
      <div className="mt-3" data-testid="post-visibility">
        <p className="sr-only" id="post-vis-label">Who can see this post</p>
        <div role="radiogroup" aria-labelledby="post-vis-label" className="inline-flex rounded-full border border-border p-0.5 text-sm font-medium">
          {[["public", "Everyone", Globe], ["friends", "Friends only", Users]].map(([v, label, Icon]) => (
            <button key={v} type="button" role="radio" aria-checked={f.visibility === v} onClick={() => set({ visibility: v })} data-testid={`post-vis-${v}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 transition-colors ${f.visibility === v ? "bg-[#C89B3C] text-black" : "text-muted-foreground hover:text-foreground"}`}>
              <Icon className="h-4 w-4" aria-hidden="true" /> {label}
            </button>
          ))}
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground" data-testid="post-vis-note">
          {f.visibility === "public" ? "Anyone can see this post and its photos, including people who aren't your friends and visitors who aren't signed in." : "Only your accepted friends can see this post."}
        </p>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">{f.body.length} / {POST_MAX}</span>
        <div className="flex items-center gap-3">
          {onCancel && <button type="button" onClick={onCancel} data-testid="post-cancel" className="text-sm underline">Cancel</button>}
          <button type="submit" disabled={busy || uploading > 0 || !f.body.trim()} data-testid="post-submit" className="rounded-full bg-[#C89B3C] px-7 py-2.5 text-base font-semibold text-black disabled:opacity-50">{busy ? "Posting…" : initial ? "Save" : "Post"}</button>
        </div>
      </div>
      {picking && ShowPicker && <ShowPicker onPick={(s) => { setPicking(false); if (!s.custom) set({ show_id: s.id, show_name: s.name }); else toast.error("Posts can only tag shows on the site."); }} onClose={() => setPicking(false)} />}
    </form>
  );
}

/** One post. Owner controls appear only when onEdit/onDelete are given. */
export function PostCard({ post, mine, showAuthor = true, canReact = true, canReport = true, canComment = true, onEdit, onDelete, onChange }) {
  const [shown, setShown] = useState(false);
  const long = (post.body || "").length > 700;
  const [open, setOpen] = useState(false);
  const text = long && !open ? `${post.body.slice(0, 700).trimEnd()}…` : post.body;
  const hide = post.spoiler && !shown;
  return (
    <article className="rounded-2xl border border-border bg-card p-5" data-testid="post-card">
      <div className="flex items-start gap-3">
        {showAuthor && <Avatar avatar={post.avatar} url={post.avatar_url} name={post.username || "?"} />}
        <div className="min-w-0 flex-1">
          <p className="text-base leading-tight">
            {showAuthor && (post.has_page === false
              ? <span className="font-semibold" data-testid="post-author">{post.username}</span>
              : <Link to={`/u/${post.username}`} className="font-semibold hover:underline" data-testid="post-author">{post.username}</Link>)}
            {post.show_name && <span className="text-muted-foreground">{showAuthor ? " on " : "On "}{post.show_slug ? <Link to={`/shows/${post.show_slug}`} className="text-[#C89B3C] underline">{post.show_name}</Link> : post.show_name}</span>}
          </p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
            <span>{when(post.created_at)}</span><StarsRead value={post.rating} />
            {mine && post.visibility === "public" && <span className="inline-flex items-center gap-1" data-testid="post-public-flag"><Globe className="h-3 w-3" /> Public</span>}
            {mine && post.hidden && <span className="rounded bg-red-500/20 px-1.5 py-0.5 text-red-300" data-testid="hidden-flag">Hidden by us</span>}
          </p>
        </div>
        {mine && onEdit && (
          <div className="flex flex-shrink-0 items-center gap-3 text-sm">
            <button type="button" onClick={() => onEdit(post)} data-testid="post-edit" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"><Pencil className="h-3.5 w-3.5" /> Edit</button>
            <button type="button" onClick={() => onDelete(post)} data-testid="post-delete" className="inline-flex items-center gap-1 text-muted-foreground hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
          </div>
        )}
      </div>
      {hide ? (
        <div className="mt-3 rounded-xl border border-dashed border-[#C89B3C]/50 bg-background/60 p-4 text-center" data-testid="post-spoiler-cover">
          <p className="text-sm text-muted-foreground">This post contains spoilers.</p>
          <button type="button" onClick={() => setShown(true)} data-testid="post-spoiler-show" className="mt-2 rounded-full border border-[#C89B3C]/60 px-4 py-1.5 text-sm font-semibold text-[hsl(var(--gold))]">Show post</button>
        </div>
      ) : (
        <div className="mt-3">
          <p className="whitespace-pre-wrap break-words text-base leading-relaxed text-foreground" data-testid="post-text">{text}</p>
          {long && <button type="button" className="mt-1 text-sm text-[#C89B3C] underline" onClick={() => setOpen(!open)}>{open ? "Show less" : "Read more"}</button>}
          <PostImages images={post.images} who={post.username} />
        </div>
      )}
      {mine
        ? <><ContentReactions kind="post" item={post} canReact={false} onChange={onChange} /><ReactedBy item={post} onRemoved={(rid) => onChange({ ...post, reacted_by: (post.reacted_by || []).filter((r) => r.id !== rid) })} /></>
        : <><ContentReactions kind="post" item={post} canReact={canReact} onChange={onChange} />{canReport && <div className="mt-3"><ReportLink kind="post" id={post.id} /></div>}</>}
      {canComment && <Comments kind="post" id={post.id} count={post.comment_count || 0} onCount={(c) => c !== (post.comment_count || 0) && onChange({ ...post, comment_count: c })} />}
    </article>
  );
}